import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SportsRoundedIcon from "@mui/icons-material/SportsRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";

import "./Dashboard.css";
import AdminLayout from "../../components/AdminLayout";
import Loader from "../../components/Loading/Loader";
import PresenceReportDialog from "../../components/PresenceReportDialog";
import TableTennisRacketIcon from "../../components/TableTennisRacketIcon";
import { getdashboard, getParticipantsWeeklyChart } from "../../components/funtionsBD/Mainservice";
import { usePermissions } from "../../context/Permissioncontext";
import { useBrand } from "../../context/BrandContext";
import { LiveTrainingReturnFab } from "../../components/LiveTrainingReturnFab";
import {
  maybeAutoPromptAdminDigiping,
} from "../../utils/digipingClubSetup";
import { currentAppLanguage } from "../../utils/appLanguages";
import { isUiTourSettledForRole } from "../../tour";
import { OPEN_PRESENCE_REPORT_EVENT, UI_TOUR_SETTLED_EVENT } from "../../tour/tourEvents";
import { useTourDemoActive } from "../../tour/useTourDemoActive";
import { demoDashboardCounts, demoParticipationWeeks, isEmptyDashboard } from "../../tour/tourDemoFixtures";
import {
  canOpenDashboardPresenceReport,
  getDashboardRoleBadgeLabel,
  readStoredUser,
} from "../../utils/homeRole";

const PARTICIPATION_RANGES = [
  { months: 1, labelKey: "chartRange1Month" },
  { months: 2, labelKey: "chartRange2Months" },
  { months: 4, labelKey: "chartRange4Months" },
  { months: 6, labelKey: "chartRange6Months" },
  { months: 12, labelKey: "chartRange1Year" },
];

export function Dashboard() {
  const [data, setdata] = useState({});
  const [participationWeeks, setParticipationWeeks] = useState([]);
  const [participationMonths, setParticipationMonths] = useState(1);
  const [participationLoading, setParticipationLoading] = useState(false);
  const [isloading, setisloading] = useState(false);
  const [presenceReportOpen, setPresenceReportOpen] = useState(false);
  const [clubSyncTick, setClubSyncTick] = useState(0);
  const [tourSettled, setTourSettled] = useState(() => isUiTourSettledForRole("admin"));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { brand, themeMode } = useBrand();
  const tourDemo = useTourDemoActive();
  void clubSyncTick;
  const info = readStoredUser() || {};
  const activeClubId =
    info?.club_id || info?.club?.club_id || info?.club_details?.club_id || "";
  const dashboardRole = getDashboardRoleBadgeLabel(info);
  const canOpenPresenceReport = canOpenDashboardPresenceReport(info);
  const dash = useMemo(() => {
    if (tourDemo && isEmptyDashboard(data)) return { ...data, ...demoDashboardCounts() };
    return data;
  }, [data, tourDemo]);
  const weeks = useMemo(() => {
    if (Array.isArray(participationWeeks) && participationWeeks.length) return participationWeeks;
    if (tourDemo) return demoParticipationWeeks();
    return participationWeeks;
  }, [participationWeeks, tourDemo]);

  const brandPrimary = brand?.color_primary || "#0877BC";
  const chartAxisColor =
    themeMode === "dark" ? "#a1a1aa" : brand?.color_text_secondary || "#838383";

  const participationCategories = useMemo(
    () => weeks.map((w) => w.label),
    [weeks]
  );

  const participationSeries = useMemo(
    () => [
      {
        name: t("weeklyParticipants", { defaultValue: "Participants" }),
        data: weeks.map((w) => w.participants || 0),
      },
    ],
    [weeks, t]
  );

  const participationChartOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: chartAxisColor,
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 500,
        },
      },
      theme: {
        mode: themeMode,
      },
      colors: [brandPrimary],
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      markers: {
        size: weeks.length <= 8 ? 4 : 0,
        strokeWidth: 2,
        hover: { size: 6 },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor:
          themeMode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        strokeDashArray: 4,
      },
      xaxis: {
        categories: participationCategories,
        labels: {
          style: { colors: chartAxisColor, fontSize: "11px" },
          rotate: participationCategories.length > 10 ? -35 : 0,
          rotateAlways: participationCategories.length > 14,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          style: { colors: chartAxisColor },
          formatter(val) {
            return Math.round(val).toString();
          },
        },
        title: {
          text: t("weeklyParticipants", { defaultValue: "Players" }),
          style: { color: chartAxisColor, fontSize: "11px", fontWeight: 600 },
        },
      },
      tooltip: {
        theme: themeMode === "dark" ? "dark" : "light",
        y: {
          formatter(val, opts) {
            const week = weeks[opts?.dataPointIndex];
            const sessions = week?.sessions != null ? week.sessions : null;
            const base = `${Math.round(val)} ${t("players")}`;
            return sessions != null
              ? `${base} · ${sessions} ${t("session")}`
              : base;
          },
        },
      },
      legend: { show: false },
    }),
    [
      brandPrimary,
      chartAxisColor,
      themeMode,
      participationCategories,
      weeks,
      t,
    ]
  );

  const canOpenUsers =
    info?.isSuperAdmin === true ||
    Number(info?.type_user_id) === 1 ||
    hasPermission("USER_LIST_VIEW");

  const loadParticipationChart = async (months = participationMonths) => {
    try {
      setParticipationLoading(true);
      const response = await getParticipantsWeeklyChart({
        club_id: activeClubId || info?.club_id,
        months,
      });
      setParticipationWeeks(response.data?.data?.weeks || []);
    } catch (error) {
      console.error("Failed to load participation chart:", error);
      setParticipationWeeks([]);
    } finally {
      setParticipationLoading(false);
    }
  };

  useEffect(() => {
    const onClubChanged = () => setClubSyncTick((n) => n + 1);
    const onTour = () => setTourSettled(true);
    const onPresence = (event) => {
      setPresenceReportOpen(event.detail?.open !== false);
    };
    window.addEventListener("smarttt-club-changed", onClubChanged);
    window.addEventListener("smarttt-user-updated", onClubChanged);
    window.addEventListener("smarttt-home-role-changed", onClubChanged);
    window.addEventListener(UI_TOUR_SETTLED_EVENT, onTour);
    window.addEventListener(OPEN_PRESENCE_REPORT_EVENT, onPresence);
    return () => {
      window.removeEventListener("smarttt-club-changed", onClubChanged);
      window.removeEventListener("smarttt-user-updated", onClubChanged);
      window.removeEventListener("smarttt-home-role-changed", onClubChanged);
      window.removeEventListener(UI_TOUR_SETTLED_EVENT, onTour);
      window.removeEventListener(OPEN_PRESENCE_REPORT_EVENT, onPresence);
    };
  }, []);

  useEffect(() => {
    getdata();
    loadParticipationChart(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClubId]);

  useEffect(() => {
    if (info?.type_user_id !== 2) return undefined;
    if (brand?.show_ai_coach === false) return undefined;
    if (!tourSettled) return undefined;
    const userId = info?.user_id;
    const clubId = activeClubId || info?.club_id;
    if (!userId || !clubId || !localStorage.getItem("token")) return undefined;

    let cancelled = false;
    (async () => {
      try {
        await maybeAutoPromptAdminDigiping({
          userId,
          clubId,
          t,
          lang: currentAppLanguage(i18n),
        });
      } catch (err) {
        if (!cancelled) console.warn("Club setup prompt failed:", err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeClubId, brand?.show_ai_coach, i18n, info?.type_user_id, info?.user_id, t, tourSettled]);

  const handleParticipationRange = (months) => {
    setParticipationMonths(months);
    loadParticipationChart(months);
  };

  const getdata = async () => {
    try {
      const response = await getdashboard(activeClubId || info?.club_id);
      setdata(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenUsers = () => navigate("/UserManagement");
  const handleOpenGroups = () => navigate("/GroupList");
  const handleOpenPresenceReport = () => setPresenceReportOpen(true);
  const showPresenceReport = Boolean(
    (canOpenPresenceReport && activeClubId) || tourDemo
  );

  return (
    <AdminLayout>
      <div className="dashboard-role-block">
          <div className="card_list" data-tour-id="admin-dashboard">
            <div
              className="card card-users card-with-role"
              data-tour-id="admin-users-card"
              onClick={canOpenUsers ? handleOpenUsers : undefined}
              onKeyDown={
                canOpenUsers
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenUsers();
                      }
                    }
                  : undefined
              }
              role={canOpenUsers ? "button" : undefined}
              tabIndex={canOpenUsers ? 0 : undefined}
              title={t("alluser")}
            >
              <div className="card_icon">
                <GroupsRoundedIcon />
              </div>
              <div className="card_data">
                <h1>{t("totalmembers")}</h1>
                <p>{dash.totalUsers || 0}</p>
              </div>
              <div className="go-corner"></div>
              <span className="card-role-badge">{dashboardRole}</span>
            </div>
            <div
              className="card card-players card-with-role"
              onClick={canOpenUsers ? handleOpenUsers : undefined}
              onKeyDown={
                canOpenUsers
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenUsers();
                      }
                    }
                  : undefined
              }
              role={canOpenUsers ? "button" : undefined}
              tabIndex={canOpenUsers ? 0 : undefined}
              title={t("alluser")}
            >
              <div className="card_icon">
                <TableTennisRacketIcon />
              </div>
              <div className="card_data">
                <h1>{t("activeplayer")}</h1>
                <p>{dash.monthlyActivePlayers || 0}</p>
              </div>
              <div className="go-corner" />
              <span className="card-role-badge">{dashboardRole}</span>
            </div>
            <div
              className="card card-coaches card-with-role"
              onClick={canOpenUsers ? handleOpenUsers : undefined}
              onKeyDown={
                canOpenUsers
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenUsers();
                      }
                    }
                  : undefined
              }
              role={canOpenUsers ? "button" : undefined}
              tabIndex={canOpenUsers ? 0 : undefined}
              title={t("totalcoaches")}
            >
              <div className="card_icon">
                <SportsRoundedIcon />
              </div>
              <div className="card_data">
                <h1>{t("totalcoaches")}</h1>
                <p>{dash.totalCoaches || 0}</p>
              </div>
              <div className="go-corner" />
              <span className="card-role-badge">{dashboardRole}</span>
            </div>
            {hasPermission("VIEW_GROUP") && (
              <div 
                className="card card-groups card-with-role"
                data-tour-id="admin-groups-card" 
                onClick={handleOpenGroups}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenGroups();
                  }
                }}
                role="button"
                tabIndex={0}
                title={t("totalgrp")}
              >
                <div className="card_icon">
                  <HubRoundedIcon />
                </div>
                <div className="card_data">
                  <h1>{t("totalgrp")}</h1>
                  <p>{dash.totalGroups || 0}</p>
                </div>
                <div className="go-corner" />
                <span className="card-role-badge">{dashboardRole}</span>
              </div>
            )}
          </div>
          <div className="chart_list chart_list--single">
            <div className="chart chart-with-role chart--participation">
              <div className="chart-header chart-header--split">
                <div className="chart-header-title">
                  <FontAwesomeIcon icon={faChartLine} style={{ marginRight: "8px" }} />
                  <h3>
                    {t("weeklyParticipantsChart", {
                      defaultValue: "Weekly participants",
                    })}
                    {dash?.totalSessions != null ? ` (${dash.totalSessions})` : ""}
                  </h3>
                </div>
                <div className="chart-header-actions">
                  <div className="chart-range-pills" role="group" aria-label={t("chartRange")}>
                    {PARTICIPATION_RANGES.map((range) => (
                      <button
                        key={range.months}
                        type="button"
                        className={`chart-range-pill${
                          participationMonths === range.months ? " chart-range-pill--active" : ""
                        }`}
                        onClick={() => handleParticipationRange(range.months)}
                        disabled={participationLoading}
                      >
                        {t(range.labelKey)}
                      </button>
                    ))}
                  </div>
                  {showPresenceReport ? (
                    <button
                      type="button"
                      className="chart-presence-open"
                      data-tour-id="admin-presence-open"
                      onClick={handleOpenPresenceReport}
                      title={t("presenceReportOpen")}
                    >
                      <AssignmentTurnedInRoundedIcon sx={{ fontSize: 16 }} />
                      <span>{t("presenceReport")}</span>
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="chart-body chart-body--participation" data-tour-id="admin-presence-chart">
                {participationLoading && participationWeeks.length === 0 ? (
                  <div className="chart-empty">{t("loading")}</div>
                ) : (
                  <Chart
                    options={participationChartOptions}
                    series={participationSeries}
                    type="area"
                    width="100%"
                    height={280}
                  />
                )}
              </div>
              <span className="chart-role-badge">{dashboardRole}</span>
            </div>
          </div>
        </div>

      {isloading && !tourDemo && <Loader />}
      <PresenceReportDialog
        open={presenceReportOpen}
        onClose={() => setPresenceReportOpen(false)}
        clubId={activeClubId || info?.club_id}
      />
      <LiveTrainingReturnFab />
    </AdminLayout>
  );
}
